import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { RoleRequestService } from 'src/app/modules/user-profile/services/role-request.service';
import { PersonDetailsDialogComponent } from '../person-details-dialog/person-details-dialog.component';
import { AlertComponent } from 'src/app/modules/shared/components/alert/alert.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { SpinnerComponent } from 'src/app/modules/shared/components/spinner/spinner.component';
import { RoleRequestSuccessComponent } from '../role-request-success/role-request-success.component';

interface RoleRequest {
  personKey: string;
  firstName: string;
  lastName: string;
  address: string;
  existingRoles: string[];
  rolesRequested: any[];
  expanded?: boolean;
}

@Component({
  selector: 'app-role-request-manage-request',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PersonDetailsDialogComponent,
    AlertComponent,
    TooltipModule,
    NgxSpinnerModule,
    SpinnerComponent,
    RoleRequestSuccessComponent
  ],
  templateUrl: './role-request-manage-request.component.html',
  styleUrls: ['./role-request-manage-request.component.css']
})
export class RoleRequestManageRequestComponent implements OnInit {
  private roleRequestService = inject(RoleRequestService);
  private spinner = inject(NgxSpinnerService);

  modalDtls = { reason: '', effectiveDate: new Date().toISOString().split('T')[0] };
  today = new Date().toISOString().split('T')[0];
  selectedReq: RoleRequest | null = null;
  requests: RoleRequest[] = [];
  showModal = false;
  showErrorMessage: string = '';
  isRequestSuccess = false;

  ngOnInit(): void {
    this.loadRoleRequests();
  }

  private loadRoleRequests() {
    this.spinner.show('manage-request');
    this.roleRequestService.getRoleRequests().subscribe(
      (requests: any[]) => {
        this.requests = this.groupRoleRequests(requests);
        this.spinner.hide('manage-request');
      },
      error => {
        this.handleError('Error fetching role requests', error);
      }
    );
  }

  private groupRoleRequests(requests: any[]): RoleRequest[] {
    const groupedData = requests.reduce((acc, item) => {
      const { personKey, firstName, lastName, address, existingRoles } = item;
      item.effectiveDate = new Date(item.effectiveDate || new Date()).toISOString().split('T')[0];
      if (!acc[personKey]) {
        existingRoles.sort((a, b) => a.localeCompare(b));
        acc[personKey] = { personKey, firstName, lastName, address, rolesRequested: [], existingRoles };
      }
      acc[personKey].rolesRequested.push({ ...item });
      return acc;
    }, {});

    return Object.values(groupedData);
  }

  clickOnUser(request: RoleRequest) {
    this.selectedReq = this.selectedReq === request ? null : request;
    if (this.selectedReq) {
      this.selectedReq.expanded = true;
    } else {
      request.expanded = false;
    }
  }

  clickOnHistoryExpand(role: any) {
    if (role) {
      role.showHistory = !role.showHistory;
      if(!role.showHistory) {
        return;
      }
      this.spinner.show('manage-request');
      this.roleRequestService.getRoleRequestHistory(role.roleRequestKey).subscribe(
        (history: any[]) => {
          role.history = history;
          this.spinner.hide('manage-request');
        },
        error => {
          this.handleError('Error fetching role request history', error);
        }
      );
    }
  }

  onAccept(event: Event, selectedRole: any) {
    this.toggleRoleSelection(event, selectedRole, 'isAccept');
  }

  onReject(event: Event, selectedRole: any) {
    this.toggleRoleSelection(event, selectedRole, 'isReject');
  }

  private toggleRoleSelection(event: Event, selectedRole: any, selectionType: 'isAccept' | 'isReject') {
    const isSelected = (event.target as HTMLInputElement).checked;
    selectedRole[selectionType] = isSelected;
    selectedRole[selectionType === 'isAccept' ? 'isReject' : 'isAccept'] = null;
    selectedRole.showCommentsSection = isSelected;
  }

  getStats(type: 'unselected' | 'approve' | 'reject'): number {
    const allRoles = this.requests.flatMap(x => x.rolesRequested);
    switch (type) {
      case 'approve':
        return allRoles.filter(x => x.isAccept).length;
      case 'reject':
        return allRoles.filter(x => x.isReject).length;
      case 'unselected':
        return allRoles.filter(x => !x.isReject && !x.isAccept).length;
      default:
        return 0;
    }
  }

  get atleastOneRoleSelected(): boolean {
    return this.getStats('approve') > 0 || this.getStats('reject') > 0;
  }

  selectAllForApprove() {
    this.updateAllRolesState(true, false);
    this.showModal = false;
  }

  showError(message: string): void {
    this.showErrorMessage = message;
    window.scrollTo(0, 0);
  }

  private updateAllRolesState(isAccept: boolean, isReject: boolean) {
    this.requests.flatMap(x => x.rolesRequested).forEach(role => {
      role.isAccept = isAccept;
      role.isReject = isReject;
      role.effectiveDate = this.modalDtls.effectiveDate;
      role.reason = this.modalDtls.reason;
    });
  }

  addComment(role) {
    this.showErrorMessage = '';
    if(role.isReject && !role.reason?.trim()) {
      this.showError('Please provide a reason.');
      return;
    }
    role.showCommentsSection = false;
  }

  onCancel(role) {
    role.showCommentsSection = false;
  }

  submitRoleUpdate() {
    this.showErrorMessage = '';
    if(this.requests.flatMap(x => x.rolesRequested.filter(x => x.isReject))
      .some(role => !role.reason?.trim())) {
      this.showError("Please provide a reason for the declined roles.");
      return;
    }

    const payload = this.requests.flatMap(x => x.rolesRequested.filter(x => x.isReject || x.isAccept).map(role => ({
      roleRequestKey: role.roleRequestKey,
      approved: !!role.isAccept,
      approverComment: role.reason || ' ',
      effectiveDate: new Date(role.effectiveDate),
    })));

    this.spinner.show('manage-request');
    this.roleRequestService.approveRoleRequests(payload).subscribe(
      () => {
        this.spinner.hide('manage-request');
        this.showErrorMessage = '';
        this.isRequestSuccess = true;
        console.log('Role updates submitted successfully');
      },
      error => {
        this.handleError(error.error.resultMessage, error);
      }
    );
  }

  onCloseAlert() {
    this.clickOnUser(this.selectedReq);
  }

  private handleError(message: string, error: any) {
    console.error(message, error);
    this.showError(message);
    this.spinner.hide('manage-request');
  }
}
